import React, { useState } from 'react';
import { ChevronRight, User, Mail, Building, Globe, Lock, CheckCircle } from 'lucide-react';
import type { ContactFormProps, ContactInfo } from '../types';
import { submitToGoogleSheets } from '../services/googleSheets';

const ContactForm: React.FC<ContactFormProps> = ({ assessmentResults, onComplete, onBack }) => {
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    firstName: '',
    lastName: '',
    email: '',
    companyUrl: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!contactInfo.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!contactInfo.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!contactInfo.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!contactInfo.companyUrl.trim()) {
      newErrors.companyUrl = 'Company URL is required';
    } else if (!contactInfo.companyUrl.startsWith('http')) {
      setContactInfo(prev => ({
        ...prev,
        companyUrl: `https://${contactInfo.companyUrl}`
      }));
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Submit to Google Sheets
      await submitToGoogleSheets(contactInfo, assessmentResults);
      
      // If successful, proceed to roadmap
      onComplete(contactInfo);
    } catch (error) {
      console.error('Submission error:', error);
      // For now, still proceed even if Google Sheets fails
      // In production, you might want to show an error message
      onComplete(contactInfo);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof ContactInfo) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setContactInfo(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center bg-brand-red/10 text-brand-red px-4 py-2 rounded-full text-sm font-medium mb-4">
          <CheckCircle className="w-4 h-4 mr-2" />
          Assessment Complete - Score: {assessmentResults.overallScore}/10
        </div>
        
        <h1 className="text-3xl font-bold text-brand-black mb-2">
          Get Your Personalized AI Roadmap
        </h1>
        <p className="text-lg text-brand-gray mb-2">
          Enter your details to receive your comprehensive 15-page implementation strategy based on the SymbioEthical Framework
        </p>
        <p className="text-sm text-brand-gray">
          Worth $2,500 in consulting value - From the Amazon Best-Selling "Amplify AI" Book
        </p>
      </div>

      {/* Security Badge */}
      <div className="bg-brand-beige/10 border border-brand-beige rounded-lg p-4 mb-6">
        <div className="flex items-center">
          <Lock className="w-5 h-5 text-brand-red mr-3" />
          <div>
            <h3 className="font-semibold text-brand-black">Your Data is Secure</h3>
            <p className="text-sm text-brand-gray">
              AmplifyAI Solutions uses enterprise-grade security and never shares your information with third parties.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white p-6 border-2 border-gray-200 rounded-xl">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h2>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                First Name *
              </label>
              <input
                type="text"
                value={contactInfo.firstName}
                onChange={handleInputChange('firstName')}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.firstName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Enter your first name"
              />
              {errors.firstName && (
                <p className="text-red-600 text-sm mt-1">{errors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                Last Name *
              </label>
              <input
                type="text"
                value={contactInfo.lastName}
                onChange={handleInputChange('lastName')}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.lastName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Enter your last name"
              />
              {errors.lastName && (
                <p className="text-red-600 text-sm mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="w-4 h-4 inline mr-1" />
              Business Email *
            </label>
            <input
              type="email"
              value={contactInfo.email}
              onChange={handleInputChange('email')}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="you@company.com"
            />
            {errors.email && (
              <p className="text-red-600 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Company URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Globe className="w-4 h-4 inline mr-1" />
              Company Website *
            </label>
            <input
              type="url"
              value={contactInfo.companyUrl}
              onChange={handleInputChange('companyUrl')}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.companyUrl ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="https://yourcompany.com"
            />
            {errors.companyUrl && (
              <p className="text-red-600 text-sm mt-1">{errors.companyUrl}</p>
            )}
          </div>
        </div>

        {/* What You'll Get */}
        <div className="bg-gradient-to-r from-brand-beige/20 to-brand-red/10 p-6 rounded-xl border-2 border-brand-beige">
          <h3 className="font-bold text-brand-black mb-3">
            <Building className="w-5 h-5 inline mr-2" />
            What You'll Receive from AmplifyAI Solutions:
          </h3>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-brand-red mt-0.5 mr-2 flex-shrink-0" />
              <span className="text-sm text-brand-gray">Complete 90-day implementation roadmap</span>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-brand-red mt-0.5 mr-2 flex-shrink-0" />
              <span className="text-sm text-brand-gray">AI tools stack recommendations</span>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-brand-red mt-0.5 mr-2 flex-shrink-0" />
              <span className="text-sm text-brand-gray">ROI projections and success metrics</span>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-brand-red mt-0.5 mr-2 flex-shrink-0" />
              <span className="text-sm text-brand-gray">Expert implementation support options</span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 text-brand-gray hover:text-brand-black transition-colors"
          >
            ← Back to Assessment
          </button>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-8 py-3 bg-brand-red text-brand-white rounded-lg font-bold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center shadow-lg"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Generating Report...
              </>
            ) : (
              <>
                Get My AI Roadmap
                <ChevronRight className="w-4 h-4 ml-2" />
              </>
            )}
          </button>
        </div>
      </form>

      {/* Trust Signals */}
      <div className="mt-8 text-center text-sm text-gray-500">
        <p>🔒 SSL Secured • No spam • Unsubscribe anytime</p>
        <p className="mt-1">Used by 500+ businesses to transform with AI</p>
      </div>
    </div>
  );
};

export default ContactForm;